import apiClient from '../client'

// Email queue item type
export interface EmailQueueItem {
  id: number
  request_id: string
  lead_id?: number
  lead_company?: string
  email_type: string
  to_email: string
  subject?: string
  status: 'pending' | 'pending_review' | 'processing' | 'sent' | 'failed' | 'cancelled' | 'rejected'
  priority: string
  source?: string
  attempts: number
  max_attempts: number
  last_error?: string
  last_attempt_at?: string
  next_retry_at?: string
  sent_at?: string
  created_at?: string
  template_data?: Record<string, unknown>
  // Review fields
  requires_review?: boolean
  reviewed_at?: string
  source_call_id?: string
}

// Email queue with call context
export interface EmailQueueItemWithContext extends EmailQueueItem {
  review_notes?: string
  transcript_excerpt?: string
  body_html?: string
  body_text?: string
  call_context?: {
    call_id: string
    recording_url?: string
    transcript?: string
    duration_seconds?: number
    answered_by?: string
    summary?: string
  }
  lead?: {
    id: number
    company_name?: string
    email?: string
    phone?: string
  }
}

// Email queue stats
export interface EmailQueueStats {
  pending: number
  pending_review: number
  processing: number
  sent: number
  failed: number
  cancelled: number
  rejected: number
  total: number
}

// Approve request
export interface EmailApproveRequest {
  review_notes?: string
  to_email?: string
  subject?: string
  contact_name?: string
  company_name?: string
}

// Reject request
export interface EmailRejectRequest {
  review_notes?: string
}

// Queue list response
interface EmailQueueListResponse {
  items: EmailQueueItem[]
  total: number
  limit: number
  offset: number
}

export const emailQueueApi = {
  // Get email queue items with optional filters
  getAll: (params?: {
    status?: string
    requires_review?: boolean
    limit?: number
    offset?: number
    search?: string
  }) => apiClient.get<EmailQueueListResponse>('/api/queue/email', { params }),

  // Get queue statistics
  getStats: () => apiClient.get<EmailQueueStats>('/api/queue/email/stats'),

  // Get single item with full context (for review modal)
  getById: (id: number) => apiClient.get<EmailQueueItemWithContext>(`/api/queue/email/${id}`),

  // Update email queue item
  update: (id: number, data: {
    to_email?: string
    subject?: string
    contact_name?: string
    company_name?: string
  }) => apiClient.put<{ success: boolean }>(`/api/queue/email/${id}`, data),

  // Approve an email for sending
  approve: (id: number, data?: EmailApproveRequest) =>
    apiClient.post<{
      success: boolean
      message: string
      id: number
      request_id?: string
      pubsub_message_id?: string
    }>(`/api/queue/email/${id}/approve`, data || {}),

  // Reject an email
  reject: (id: number, data?: EmailRejectRequest) =>
    apiClient.post<{
      success: boolean
      message: string
      id: number
    }>(`/api/queue/email/${id}/reject`, data || {}),

  // Cancel a pending email
  cancel: (id: number) => apiClient.post<{ success: boolean }>(`/api/queue/email/${id}/cancel`),

  // Retry a failed email
  retry: (id: number) => apiClient.post<{ success: boolean }>(`/api/queue/email/${id}/retry`),

  // Resend a sent or failed email
  resend: (id: number, data?: { to_email?: string; subject?: string }) =>
    apiClient.post<{
      success: boolean
      message: string
      original_id: number
      new_id: number
      request_id?: string
      pubsub_message_id?: string
    }>(`/api/queue/email/${id}/resend`, data || {}),

  // Delete a cancelled/failed/rejected email
  delete: (id: number) => apiClient.delete(`/api/queue/email/${id}`),

  // Batch delete multiple email queue items
  batchDelete: (ids: number[]) =>
    apiClient.post<{
      success: boolean
      message: string
      deleted_count: number
      deleted_ids: number[]
      skipped_count: number
      skipped?: { id: number; status: string; reason: string }[]
    }>('/api/queue/email/batch-delete', { ids }),

  // Refresh email with active template
  refreshTemplate: (id: number) =>
    apiClient.post<{
      success: boolean
      message: string
      id: number
      template_id: number
      template_name: string
      template_version: string
      subject: string
      body_html: string
      body_text?: string
    }>(`/api/queue/email/${id}/refresh-template`),

  // AI refine email content based on call transcript
  aiRefine: (id: number, instructions?: string) =>
    apiClient.post<{
      success: boolean
      message: string
      id: number
      subject: string
      body_html: string
      changes_made: string[]
    }>(`/api/queue/email/${id}/ai-refine`, instructions ? { instructions } : {}),
}
